import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { HeroSection as HeroSectionType, CallToAction } from '@/lib/gcc-content'
import heroSideImage from '@/assets/hero-side-image.png'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { ChevronDown } from 'lucide-react'

interface HeroSectionProps {
  headline: string
  subtext: string
  primaryCTA: CallToAction
  secondaryCTA: CallToAction
  backgroundImage?: string
  className?: string
}

// Alternative interface for when passed as hero object
interface HeroSectionPropsWithObject {
  hero: HeroSectionType
  className?: string
}

type HeroSectionAllProps = HeroSectionProps | HeroSectionPropsWithObject

const HeroSection = (props: HeroSectionAllProps) => {
  const className = 'className' in props ? (props.className || '') : ''
  
  // Handle both prop patterns
  let hero: HeroSectionType
  if ('hero' in props) {
    hero = props.hero
  } else {
    hero = {
      headline: props.headline,
      subtext: props.subtext,
      primaryCTA: props.primaryCTA,
      secondaryCTA: props.secondaryCTA,
      backgroundImage: props.backgroundImage
    }
  }
  return (
    <section 
      data-testid="hero"
      className={`relative w-full min-h-screen ${className}`}
    >
      {/* Background gradient replacing MeshGradient shader */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#E0EAFF] via-[#241D9A] via-[#9354CE] to-[#43225A] animate-gradient-slow" />
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900/60 via-slate-900/50 to-slate-900/70" />
      
      {/* Hero content container */}
      <div className="relative z-10 h-full min-h-screen grid grid-cols-1 lg:grid-cols-2">
        {/* Left side - Content */}
        <div className="flex items-center justify-center lg:justify-center px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center lg:text-left text-white max-w-2xl lg:ml-64">
            {/* Main Headline */}
            <h1 className="text-4xl font-light mb-8 sm:text-5xl lg:text-6xl tracking-tight leading-tight">
              {hero.headline}
            </h1>
            
            {/* Subtext */}
            <p className="text-xl mb-12 leading-relaxed opacity-90 font-light max-w-lg">
              {hero.subtext}
            </p>
            
            {/* CTA Dropdown */}
            <div
              data-testid="hero-cta-container"
              className="flex flex-col sm:flex-row lg:justify-start sm:justify-center"
            >
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="outline"
                    size="lg"
                    className="w-full sm:w-auto bg-transparent border-white/60 text-white hover:bg-white/15 hover:border-white/80 shadow-lg rounded-lg px-8 py-3 font-medium tracking-tight transition-all duration-300"
                  >
                    Join Today
                    <ChevronDown className="ml-2 w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="center" className="min-w-[200px]">
                  <DropdownMenuItem asChild>
                    <Link
                      to="/auth?mode=signup&role=provider&next=/get-started"
                      className="cursor-pointer"
                    >
                      Join as Provider
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link
                      to="/auth?mode=signup&role=gcc&next=/gcc-dashboard"
                      className="cursor-pointer"
                    >
                      Join as GCC
                    </Link>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
        
        {/* Right side - User's image */}
        <div className="relative hidden lg:flex items-center justify-center">
          <div className="w-full h-96 lg:h-full max-w-lg flex items-center justify-center">
            <img 
              src={heroSideImage} 
              alt="AI-First GCC Platform Visualization" 
              className="w-full h-auto object-contain max-h-full drop-shadow-2xl"
            />
          </div>
        </div>
      </div>
    </section>
  )
}

export default HeroSection